import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../elements/Button';
import Icon from '../common/Icon';
import VoiceNote from './VoiceNote';
import PersonFields from './PersonFields';
import { apiErrorText } from '../../utils';

/* "I was here" — one sheet, one save, five records behind it.

   Everything is pre-filled before the rep sees it: the follow-up title names the
   company, the due date is a week out, the stage stays where it is unless they move
   it. What they actually have to do is tick who they spoke to and say a sentence.
   That is the whole design goal — the visit that gets logged is the one that takes
   ten seconds in a doorway. */
const dateInputValue = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const VisitSheet = ({ company, buId, onClose, onSubmit, onAddPerson }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [spokeTo, setSpokeTo] = useState([]);
  // People created from inside this sheet. Kept locally rather than reloading the
  // company: a reload mid-visit would throw away the note being dictated.
  const [added, setAdded] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newPerson, setNewPerson] = useState({});
  const [addError, setAddError] = useState(null);
  const [addingBusy, setAddingBusy] = useState(false);
  const [createTask, setCreateTask] = useState(true);
  const [taskTitle, setTaskTitle] = useState(
    t('salesFollowUpVisit', { company: company.name }));
  const [taskDue, setTaskDue] = useState(
    dateInputValue(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const [statusId, setStatusId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const togglePerson = (id) => setSpokeTo((prev) => (
    prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  // Everyone tickable: the roster the server sent, plus whoever was just created
  // here. The server's order (starred decision-maker first) is left alone.
  const people = [...(company.contacts || []), ...added];

  const savePerson = async () => {
    if (addingBusy) return;
    setAddingBusy(true);
    setAddError(null);
    try {
      // Filed as an employee: the person who opened the door works there, but
      // that is not the same as being a lead somebody is working. The rep can
      // promote them later from the company card.
      const person = await onAddPerson({ ...newPerson, role: 'employee' });
      setAdded((prev) => [...prev, { id: person.id, name: person.name }]);
      // Auto-tick them — you only add someone here because you just spoke to them.
      setSpokeTo((prev) => [...prev, person.id]);
      setNewPerson({});
      setAdding(false);
    } catch (err) {
      setAddError(apiErrorText(t, err, 'somethingWentWrong'));
    } finally {
      setAddingBusy(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        note,
        person_ids: spokeTo,
        create_task: createTask,
        task_title: taskTitle,
        // A date input gives a plain day; the API parses ISO and fills in midnight.
        task_due_at: createTask && taskDue ? taskDue : null,
        status_id: statusId || null,
      });
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
      setSaving(false);
    }
  };

  return (
    <div className="sales-sheet" role="dialog" aria-modal="true">
      <div className="sales-sheet__panel">
        <div className="sales-sheet__head">
          <h5>{t('salesIWasHere')}</h5>
          <button type="button" className="sales-sheet__close" onClick={onClose}
                  aria-label={t('close')}>
            <Icon name="cross" />
          </button>
        </div>

        <div className="sales-sheet__body">
          <p className="sales-sheet__company">{company.name}</p>

          <label className="sales-label">{t('salesWhoDidYouSpeakTo')}</label>
          <div className="sales-chips">
            {people.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`sales-chip ${spokeTo.includes(c.id) ? 'is-on' : ''}`}
                onClick={() => togglePerson(c.id)}
              >
                {c.name}
                {c.is_primary_dmu && <span className="sales-chip__star">★</span>}
              </button>
            ))}
            {/* The person who opened the door is usually not in the CRM yet.
                Without this the rep either logs the visit against nobody or
                leaves the sheet to add them and loses what they had typed. */}
            <button
              type="button"
              className="sales-chip sales-chip--add"
              onClick={() => setAdding((v) => !v)}
              aria-label={t('salesAddSomeoneNew')}
            >
              <Icon name="plus" /> {t('salesAddSomeoneNew')}
            </button>
          </div>
          {!people.length && !adding && (
            <p className="sales-hint">{t('salesNoContactsYet')}</p>
          )}

          {adding && (
            <div className="sales-sub sales-sub--boxed">
              <p className="sales-hint">{t('salesFiledAsEmployee')}</p>
              <PersonFields value={newPerson} onChange={setNewPerson}
                            buId={buId} showRole={false} />
              {addError && <p className="sales-error">{addError}</p>}
              <div className="sales-actions">
                <Button theme="secondary" mb="0" styles={{ width: '40%' }}
                        onClick={() => { setAdding(false); setAddError(null); }}>
                  {t('cancel')}
                </Button>
                <Button mb="0" styles={{ width: '58%' }} onClick={savePerson}
                        disabled={addingBusy || !newPerson.email
                                  || !newPerson.first_name || !newPerson.name}>
                  {addingBusy ? t('saving') : t('salesAddPerson')}
                </Button>
              </div>
            </div>
          )}
          {/* Nobody spoken to is a real outcome — a gatekeeper, a closed door — and
              the visit is still recorded on the company. Say so, so an empty
              selection doesn't read as an unfinished form. */}
          {!spokeTo.length && <p className="sales-hint">{t('salesSpokeToNobody')}</p>}

          <VoiceNote
            label={t('salesWhatHappened')}
            value={note}
            onChange={setNote}
            placeholder={t('salesNotePlaceholder')}
          />

          <label className="sales-check">
            <input type="checkbox" checked={createTask}
                   onChange={(e) => setCreateTask(e.target.checked)} />
            <span>{t('salesCreateFollowUp')}</span>
          </label>
          {createTask && (
            <div className="sales-sub">
              <input className="sales-input" value={taskTitle}
                     onChange={(e) => setTaskTitle(e.target.value)} />
              <input className="sales-input" type="date" value={taskDue}
                     onChange={(e) => setTaskDue(e.target.value)} />
            </div>
          )}

          {!!company.available_statuses?.length && (
            <label className="sales-label sales-label--inline">
              {t('salesMoveStage')}
              <select className="sales-input" value={statusId}
                      onChange={(e) => setStatusId(e.target.value)}>
                <option value="">
                  {t('salesStageUnchanged', { stage: company.status?.name || '—' })}
                </option>
                {company.available_statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
          )}

          {error && <p className="sales-error">{error}</p>}
        </div>

        <div className="sales-sheet__foot">
          <Button theme="secondary" onClick={onClose} mb="0" styles={{ width: '40%' }}>
            {t('cancel')}
          </Button>
          <Button onClick={save} disabled={saving} mb="0" styles={{ width: '58%' }}>
            {saving ? t('saving') : t('salesLogVisit')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VisitSheet;
